/**
 * Утилиты для нормализации YAML значений
 * Учитывают особенности студенческих ошибок при работе с YAML
 */

/**
 * Нормализует значение для корректного сравнения
 */
export function normalizeValue(value: any): any {
  if (value === null || value === undefined) {
    return value;
  }

  // Строки - убираем лишние пробелы
  if (typeof value === 'string') {
    const trimmed = value.trim();
    
    // Попытаемся преобразовать строковые числа
    if (/^\d+$/.test(trimmed)) {
      return parseInt(trimmed, 10);
    }
    
    if (/^\d+\.\d+$/.test(trimmed)) {
      return parseFloat(trimmed);
    }
    
    // Преобразование строковых boolean значений
    const lowerTrimmed = trimmed.toLowerCase();
    if (lowerTrimmed === 'true' || lowerTrimmed === 'yes' || lowerTrimmed === 'on') {
      return true;
    }
    if (lowerTrimmed === 'false' || lowerTrimmed === 'no' || lowerTrimmed === 'off') {
      return false;
    }
    
    return trimmed;
  }

  // Массивы - рекурсивно нормализуем элементы
  if (Array.isArray(value)) {
    return value.map(normalizeValue);
  }

  // Объекты - рекурсивно нормализуем значения
  if (typeof value === 'object') {
    const normalized: any = {};
    for (const [key, val] of Object.entries(value)) {
      normalized[key] = normalizeValue(val);
    }
    return normalized;
  }

  return value;
}

/**
 * Умное сравнение значений с учетом нормализации
 */
export function smartEquals(actual: any, expected: any): boolean {
  const normalizedActual = normalizeValue(actual);
  const normalizedExpected = normalizeValue(expected);
  
  return deepEquals(normalizedActual, normalizedExpected);
}

/**
 * Глубокое сравнение объектов
 */
function deepEquals(a: any, b: any): boolean {
  if (a === b) return true;
  
  if (a == null || b == null) return a === b;
  
  if (typeof a !== typeof b) return false;
  
  if (typeof a !== 'object') return a === b;
  
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  
  if (Array.isArray(a)) {
    if (a.length !== b.length) return false;
    return a.every((item, index) => deepEquals(item, b[index]));
  }
  
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  
  if (keysA.length !== keysB.length) return false;
  
  return keysA.every(key => deepEquals(a[key], b[key]));
}

/**
 * Проверяет является ли значение "числовым" (включая строки-числа)
 */
export function isNumeric(value: any): boolean {
  if (typeof value === 'number') return true;
  
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return /^\d+(\.\d+)?$/.test(trimmed);
  }
  
  return false;
}

/**
 * Преобразует значение в число, если возможно
 */
export function toNumber(value: any): number | null {
  if (typeof value === 'number') return value;
  
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d+$/.test(trimmed)) {
      return parseInt(trimmed, 10);
    }
    if (/^\d+\.\d+$/.test(trimmed)) {
      return parseFloat(trimmed);
    }
  }
  
  return null;
}

/**
 * Проверяет является ли значение "булевым" (включая строки-булевы)
 */
export function isBoolean(value: any): boolean {
  if (typeof value === 'boolean') return true;
  
  if (typeof value === 'string') {
    const lower = value.trim().toLowerCase();
    return ['true', 'false', 'yes', 'no', 'on', 'off'].includes(lower);
  }
  
  return false;
}

/**
 * Преобразует значение в boolean, если возможно
 */
export function toBoolean(value: any): boolean | null {
  if (typeof value === 'boolean') return value;
  
  if (typeof value === 'string') {
    const lower = value.trim().toLowerCase();
    if (['true', 'yes', 'on'].includes(lower)) return true;
    if (['false', 'no', 'off'].includes(lower)) return false;
  }
  
  return null;
}

/**
 * Нормализует путь к полю (обрабатывает массивы и специальные символы)
 */
export function normalizePath(path: string): string {
  return path
    .replace(/\[(\d+)\]/g, '.$1')  // containers[0] -> containers.0
    .replace(/^\./, '')           // .spec -> spec
    .replace(/\.$/, '');          // spec. -> spec
}

/**
 * Валидирует YAML на предмет частых студенческих ошибок
 */
export function validateYamlFormat(yamlString: string): string[] {
  const warnings: string[] = [];
  const lines = yamlString.split('\n');
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const lineNum = i + 1;
    
    // Проверяем табы вместо пробелов
    if (line.includes('\t')) {
      warnings.push(`Строка ${lineNum}: Используйте пробелы вместо табов для отступов`);
    }
    
    // Проверяем смешанные отступы
    const leadingSpaces = line.match(/^(\s*)/)?.[1] || '';
    if (leadingSpaces.length > 0 && leadingSpaces.length % 2 !== 0) {
      warnings.push(`Строка ${lineNum}: Рекомендуется использовать четное количество пробелов для отступов`);
    }
    
    // Проверяем лишние пробелы в конце строки
    if (line.endsWith(' ') && line.trim() !== '') {
      warnings.push(`Строка ${lineNum}: Лишние пробелы в конце строки`);
    }
    
    // Проверяем кавычки вокруг чисел
    const numberInQuotes = line.match(/:\s*"(\d+)"/);
    if (numberInQuotes) {
      warnings.push(`Строка ${lineNum}: Число ${numberInQuotes[1]} в кавычках. Для чисел кавычки обычно не нужны`);
    }
  }
  
  return warnings;
}

/**
 * Предлагает исправления для частых ошибок
 */
export function suggestFixes(yamlString: string): string[] {
  const suggestions: string[] = [];
  
  // Проверяем на строковые boolean значения
  if (yamlString.includes('"true"') || yamlString.includes('"false"')) {
    suggestions.push('💡 Для boolean значений (true/false) кавычки обычно не нужны');
  }
  
  // Проверяем на строковые числа в важных полях
  if (/replicas:\s*"[0-9]+"/.test(yamlString)) {
    suggestions.push('💡 replicas должно быть числом без кавычек: replicas: 3');
  }
  
  if (/port:\s*"[0-9]+"/.test(yamlString)) {
    suggestions.push('💡 port должен быть числом без кавычек: port: 8080');
  }
  
  // Проверяем на отсутствие обязательных полей
  if (!yamlString.includes('apiVersion:')) {
    suggestions.push('⚠️ Не найдено поле apiVersion - обязательное для Kubernetes манифестов');
  }
  
  if (!yamlString.includes('kind:')) {
    suggestions.push('⚠️ Не найдено поле kind - обязательное для Kubernetes манифестов');
  }
  
  return suggestions;
}
